import React, { useEffect, useState } from 'react';
import { YInput } from '../../../ui/YInput';
import { YSheet } from '../../../ui/sheet/YSheet';
import feedingService from '../services/feedingService';
import { hopperOverfillWarning } from '../utils/hopperCapacity';
import { isProRequiredError, shouldGateFeedWrite } from '../../pro/entitlements';
import { ProPanel } from '../../pro/ProPanel';
import {
  CLUB_FEED_TYPES,
  CLUB_FILL_NOTE_MAX,
  CLUB_FILL_STEP,
  clubFeedNotifyLine,
  clubFillAmountAfterStep,
  clubFillCtaLabel,
  clubFillDefaultAmount,
  clubFillHeadroom,
  clubFillHopperCaption,
  clubFillSignedAmount,
  clubFillStepCap,
  defaultClubLogFeedType,
} from '../utils/clubLogFill';
import './ClubLogFillDialog.css';

export function ClubLogFillDialog({
  show = false,
  kind = 'club',
  station = null,
  clubName = '',
  memberCount = 0,
  memberName = '',
  canEnableFeed = true,
  onHide,
  onLogged,
}) {
  const [amount, setAmount] = useState(0);
  const [removing, setRemoving] = useState(false);
  const [note, setNote] = useState('');
  const [feedType, setFeedType] = useState(defaultClubLogFeedType(null));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showPro, setShowPro] = useState(false);

  useEffect(() => {
    if (!show || !station) return undefined;
    setRemoving(false);
    setAmount(clubFillDefaultAmount(clubFillHeadroom(station)));
    setFeedType(defaultClubLogFeedType(station));
    setNote('');
    setSubmitting(false);
    setError(null);
    setShowPro(false);
    return undefined;
  }, [show, station]);

  const personal = kind === 'personal';
  const gated = personal && shouldGateFeedWrite(canEnableFeed);
  const headroom = clubFillHeadroom(station);
  const remaining = Math.max(0, Number(station?.estimated_remaining_lbs) || 0);
  const stepCap = clubFillStepCap({
    removing,
    headroom,
    remainingLbs: station?.estimated_remaining_lbs,
  });
  const signedAmount = clubFillSignedAmount(amount, removing);
  const overfill = hopperOverfillWarning({
    amountLbs: signedAmount,
    capacityLbs: station?.feed_capacity_lbs,
    remainingLbs: station?.estimated_remaining_lbs,
  });
  const hopper = clubFillHopperCaption(station?.feed_capacity_lbs ?? null, headroom);
  const canSubmit = Boolean(station) && signedAmount !== 0 && !overfill && !submitting;
  const capacity = Number(station?.feed_capacity_lbs);
  const showHopper = Number.isFinite(capacity) && capacity > 0;
  const havePct = showHopper ? Math.min(100, (remaining / capacity) * 100) : 0;
  const deltaPct = showHopper ? Math.min(havePct, (amount / capacity) * 100) : 0;
  const addPct = showHopper && !removing ? Math.min(100 - havePct, (amount / capacity) * 100) : 0;
  const sub = personal
    ? (station?.name || '')
    : [station?.name, clubName].filter(Boolean).join(' · ');

  const setMode = (nextRemoving) => {
    setRemoving(nextRemoving);
    const cap = clubFillStepCap({
      removing: nextRemoving,
      headroom,
      remainingLbs: station?.estimated_remaining_lbs,
    });
    setAmount(clubFillDefaultAmount(cap));
    setError(null);
  };

  const submit = async () => {
    if (!station || !canSubmit) return;
    if (shouldGateFeedWrite(canEnableFeed)) {
      setShowPro(true);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const payload = { amount_lbs: signedAmount, feed_type: feedType };
      const trimmed = note.trim();
      if (trimmed) payload.note = trimmed;
      await feedingService.addFeedEvent({ kind: personal ? 'personal' : 'club', id: station.id }, payload);
      onHide?.();
      await onLogged?.(signedAmount);
    } catch (err) {
      if (isProRequiredError(err)) {
        setShowPro(true);
        setSubmitting(false);
        return;
      }
      setError(removing ? 'Could not remove feed' : 'Could not log fill');
      setSubmitting(false);
    }
  };

  return (
    <YSheet
      show={show}
      onHide={onHide}
      kicker="LOG A FILL"
      title={sub || 'Feed site'}
      testId="club-log-fill-dialog"
      className="club-log-fill-dialog"
      bodyClassName="club-log-fill-dialog__body"
      error={gated || showPro ? null : (error || overfill)}
    >
      {gated || showPro ? (
        <ProPanel onDismiss={onHide} />
      ) : (
      <>
      <div className="club-log-fill__signs" role="group" aria-label="Add or remove feed">
        <button
          type="button"
          className={`club-log-fill__sign${!removing ? ' is-add' : ''}`}
          data-testid="club-log-fill-sign-add"
          aria-pressed={!removing}
          disabled={submitting}
          onClick={() => setMode(false)}
        >
          ADD
        </button>
        <button
          type="button"
          className={`club-log-fill__sign${removing ? ' is-remove' : ''}`}
          data-testid="club-log-fill-sign-remove"
          aria-pressed={removing}
          disabled={submitting}
          onClick={() => setMode(true)}
        >
          REMOVE
        </button>
      </div>
      <div className="club-log-fill__stepper">
        <div className="club-log-fill__row">
          <button
            type="button"
            className="club-log-fill__step"
            data-testid="club-log-fill-minus"
            aria-label={`Remove ${CLUB_FILL_STEP} pounds`}
            disabled={amount <= 0 || submitting}
            onClick={() => setAmount((current) => clubFillAmountAfterStep(current, -CLUB_FILL_STEP, stepCap))}
          >
            −
          </button>
          <div className="club-log-fill__amount">
            <span className="club-log-fill__lbs" data-testid="club-log-fill-amount">
              {removing && amount > 0 ? `−${amount}` : amount}
            </span>
            <span className="club-log-fill__unit">POUNDS</span>
          </div>
          <button
            type="button"
            className="club-log-fill__step club-log-fill__step--add"
            data-testid="club-log-fill-plus"
            aria-label={`Add ${CLUB_FILL_STEP} pounds`}
            disabled={submitting || amount >= stepCap}
            onClick={() => setAmount((current) => clubFillAmountAfterStep(current, CLUB_FILL_STEP, stepCap))}
          >
            +
          </button>
        </div>
        {showHopper ? (
          <>
            <div className="club-log-fill__bar" aria-hidden>
              {removing ? (
                <>
                  <div className="club-log-fill__have" style={{ width: `${Math.max(0, havePct - deltaPct)}%` }} />
                  <div className="club-log-fill__take" style={{ width: `${deltaPct}%` }} />
                </>
              ) : (
                <>
                  <div className="club-log-fill__have" style={{ width: `${havePct}%` }} />
                  <div className="club-log-fill__add" style={{ width: `${addPct}%` }} />
                </>
              )}
            </div>
            {hopper ? <div className="club-log-fill__hopper">{hopper}</div> : null}
          </>
        ) : null}
      </div>

      <div className="club-log-fill__kicker">FEED TYPE</div>
      <div className="club-log-fill__types" data-testid="club-log-fill-types">
        {CLUB_FEED_TYPES.map((option) => {
          const on = feedType === option.value;
          return (
            <button
              key={option.value}
              type="button"
              className={['club-log-fill__type', on ? 'club-log-fill__type--on' : '']
                .filter(Boolean)
                .join(' ')}
              data-testid={`club-log-fill-type-${option.value.replace(/[^a-z0-9]+/g, '-')}`}
              aria-pressed={on}
              onClick={() => setFeedType(option.value)}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      <div className="club-log-fill__kicker">NOTE (OPTIONAL)</div>
      <YInput
        id="club-log-fill-note"
        data-testid="club-log-fill-note"
        value={note}
        maxLength={CLUB_FILL_NOTE_MAX}
        placeholder="Bear got into the last load…"
        onChange={(event) => setNote(event.target.value.slice(0, CLUB_FILL_NOTE_MAX))}
      />

      {personal ? null : (
      <div className="club-log-fill__notify" data-testid="club-log-fill-notify">
        {clubFeedNotifyLine(memberCount, removing)}
      </div>
      )}

      <button
        type="button"
        className="club-log-fill__cta"
        data-testid="club-log-fill-submit"
        disabled={!canSubmit}
        onClick={submit}
      >
        {clubFillCtaLabel(memberName, removing, personal)}
      </button>
      </>
      )}
    </YSheet>
  );
}

export default ClubLogFillDialog;
